import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Star, Crown, Sparkles, Palette, Zap, Eye, TrendingUp, Award } from 'lucide-react';

const TIER_CONFIG = {
  bronze: { label: 'Bronze', color: 'bg-amber-700', textColor: 'text-amber-700 dark:text-amber-400', bgLight: 'bg-amber-50 border-amber-200 dark:bg-slate-900/80 dark:border-slate-800 dark:backdrop-blur-sm', range: '50–300' },
  silver: { label: 'Silber', color: 'bg-gray-400', textColor: 'text-gray-500 dark:text-gray-400', bgLight: 'bg-gray-50 border-gray-200 dark:bg-slate-900/80 dark:border-slate-800 dark:backdrop-blur-sm', range: '500–1.500' },
  gold: { label: 'Gold', color: 'bg-yellow-500', textColor: 'text-yellow-600 dark:text-yellow-400', bgLight: 'bg-yellow-50 border-yellow-200 dark:bg-slate-900/80 dark:border-slate-800 dark:backdrop-blur-sm', range: '2.000–5.000' },
};

const ITEM_ICONS = {
  avatar_emoji: Sparkles,
  profile_frame: Palette,
  stat_details: Eye,
  custom_title: Award,
  name_color: Palette,
  early_access: Zap,
  market_boost: TrendingUp,
  pro_badge: Crown,
  animated_frame: Star,
};

// Curated set of emoji choices for the Avatar-Emoji item plus a
// free-form input so users can paste anything (incl. country flags or
// the latest Unicode emoji that we didn't ship in the grid).
const AVATAR_EMOJI_CHOICES = [
  '😀','😎','🤩','🥳','🤔','🚀','🔥','⚡','💎','🏆',
  '🎯','🎲','🍀','🌟','⭐','🦄','🐺','🦁','🐉','🦊',
  '🐼','🦅','🐳','🐢','🐧','🦋','🌈','🌍','🌙','☀️',
  '⚽','🏀','🎮','🎵','📈','💰','💼','🎨','📚','✨',
];

// Generic chip-Picker für Items mit fester Variant-Liste (Farben,
// Frame-Stile etc.). Speichert sofort beim Klick.
const VariantChips = ({ choices, currentValue, onPick }) => (
  <div className="flex flex-wrap gap-2">
    {choices.map((c) => (
      <button
        key={c.value}
        type="button"
        onClick={() => onPick(c.value)}
        className={`px-3 py-1 text-xs rounded-full border transition-colors ${
          currentValue === c.value
            ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-medium dark:border-emerald-500/50 dark:bg-emerald-500/15 dark:text-emerald-300'
            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:bg-slate-800/60 dark:text-gray-300 dark:hover:bg-slate-800'
        }`}
      >
        <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${c.swatch}`} />
        {c.label}
      </button>
    ))}
  </div>
);

// Text-Picker für items mit freiem Text (custom_title). Layout
// vertikal damit das in den schmalen Shop-Cards (~280px) sauber passt.
const TextVariantPicker = ({ currentValue, maxLength, placeholder, onSave }) => {
  const [val, setVal] = useState(currentValue || '');
  useEffect(() => { setVal(currentValue || ''); }, [currentValue]);
  const dirty = val.trim() && val.trim() !== (currentValue || '');
  return (
    <div className="space-y-2">
      <input
        type="text"
        value={val}
        maxLength={maxLength}
        onChange={(e) => setVal(e.target.value)}
        placeholder={placeholder}
        className="w-full text-sm px-2 py-1 border border-gray-200 rounded dark:border-slate-700"
      />
      <button
        type="button"
        disabled={!dirty}
        onClick={() => onSave(val.trim())}
        className="w-full text-xs py-1.5 bg-emerald-500 text-white rounded disabled:opacity-50"
      >
        Speichern
      </button>
    </div>
  );
};

const NAME_COLOR_CHOICES = [
  { value: 'gold', label: 'Gold', swatch: 'bg-yellow-400' },
  { value: 'silver', label: 'Silber', swatch: 'bg-gray-400' },
  { value: 'neon', label: 'Neon', swatch: 'bg-pink-500' },
];

const FRAME_COLOR_CHOICES = [
  { value: 'green', label: 'Grün', swatch: 'bg-emerald-500' },
  { value: 'blue', label: 'Blau', swatch: 'bg-blue-500' },
  { value: 'red', label: 'Rot', swatch: 'bg-red-500' },
  { value: 'gold', label: 'Gold', swatch: 'bg-yellow-500' },
];

const EmojiPickerInline = ({ currentValue, onPick }) => {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState('');
  return (
    <div className="rounded-lg border border-gray-100 bg-white dark:bg-slate-900/80 dark:border-slate-800">
      <button
        type="button"
        className="w-full flex items-center justify-between px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 rounded-lg dark:text-gray-400 dark:hover:bg-slate-800/60"
        onClick={() => setOpen(!open)}
      >
        <span className="flex items-center gap-2">
          <span className="text-lg leading-none">{currentValue || '😀'}</span>
          <span>{currentValue ? 'Emoji ändern' : 'Emoji wählen'}</span>
        </span>
        <span className="text-gray-400 dark:text-gray-500">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-2">
          <div className="grid grid-cols-10 gap-1">
            {AVATAR_EMOJI_CHOICES.map((e) => (
              <button
                key={e}
                type="button"
                className={`text-lg p-0.5 rounded hover:bg-gray-100 ${currentValue === e ? 'bg-emerald-100' : ''}`}
                onClick={() => { onPick(e); setOpen(false); }}
                aria-label={`Emoji ${e}`}
              >
                {e}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              maxLength={8}
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Eigenes Emoji"
              className="flex-1 text-sm px-2 py-1 border border-gray-200 rounded dark:border-slate-700"
            />
            <button
              type="button"
              className="text-xs px-2 py-1 bg-emerald-500 text-white rounded disabled:opacity-50"
              disabled={!custom.trim()}
              onClick={() => { onPick(custom.trim()); setCustom(''); setOpen(false); }}
            >
              Setzen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ShopPage = () => {
  // Pull credits from AuthContext — the single source of truth. Calling
  // refreshCredits() after a purchase keeps Header / Shop / BetDialog
  // in sync without each page maintaining its own balance state.
  const { user, credits, refreshCredits, refreshCosmetics } = useAuth();
  const [items, setItems] = useState([]);
  const [myItems, setMyItems] = useState([]);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [itemsRes, myItemsRes] = await Promise.all([
          api.shop.getItems(),
          user ? api.shop.getMyItems() : Promise.resolve({ data: [] })
        ]);
        setItems(itemsRes.data);
        setMyItems(myItemsRes.data);
      } catch {}
    };
    fetch();
  }, [user]);

  const ownedItemIds = new Set(myItems.map(i => i.item_id));
  const ownedItemMap = {};
  myItems.forEach(i => { ownedItemMap[i.item_id] = i; });

  const handlePurchase = async (item) => {
    if (!user) { toast.error('Bitte einloggen'); return; }
    setPurchasing(item.id);
    try {
      await api.shop.purchaseByQuery(item.id);
      toast.success(`${item.name} gekauft!`);
      const myRes = await api.shop.getMyItems();
      setMyItems(myRes.data); refreshCosmetics?.();
      // Pull the new server-side balance into AuthContext so header +
      // shop both reflect the deduction immediately.
      refreshCredits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaufen fehlgeschlagen');
    } finally {
      setPurchasing(null);
    }
  };

  const balance = credits;

  // Creator-only items: market-boost ergibt für reine Player keinen
  // Sinn (es featured einen eigenen Markt). Für Nicht-Creator
  // verstecken, statt mit "gesperrt"-Label zu verwirren.
  const isCreator = !!user?.roles?.includes('creator');
  const CREATOR_ONLY_EFFECTS = new Set(['market_boost']);
  const visibleItems = items.filter(it => isCreator || !CREATOR_ONLY_EFFECTS.has(it.effect));

  const grouped = { bronze: [], silver: [], gold: [] };
  visibleItems.forEach(item => {
    if (grouped[item.tier]) grouped[item.tier].push(item);
  });

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Shop</h1>
        <p className="text-gray-500 mt-1 dark:text-gray-400">Virtuelle Items — personalisiere dein Profil</p>
        {user && (
          <div className="mt-3 inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold dark:bg-emerald-500/10 dark:text-emerald-400" data-testid="shop-balance">
            Dein Guthaben: {balance.toFixed(0)} Credits
          </div>
        )}
      </div>

      {Object.entries(grouped).map(([tier, tierItems]) => {
        const cfg = TIER_CONFIG[tier];
        if (tierItems.length === 0) return null;

        return (
          <div key={tier} className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className={`${cfg.color} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                {cfg.label}
              </span>
              <span className="text-sm text-gray-400 dark:text-gray-500">{cfg.range} Credits</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tierItems.map(item => {
                const owned = ownedItemIds.has(item.id);
                const IconComp = ITEM_ICONS[item.effect] || Star;

                return (
                  <Card key={item.id} className={`border ${cfg.bgLight} transition-shadow hover:shadow-md overflow-hidden`} data-testid={`shop-item-${item.id}`}>
                    {/* Cover image: optional, set by admin. Falls back
                        to the icon-only header when image_url is empty. */}
                    {item.image_url && (
                      <div className="w-full h-24 bg-gray-100 overflow-hidden dark:bg-slate-800">
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-9 h-9 rounded-lg ${cfg.color} bg-opacity-10 flex items-center justify-center`}>
                          <IconComp size={18} className={cfg.textColor} />
                        </div>
                        <div>
                          <CardTitle className="text-sm">{item.name}</CardTitle>
                          <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400">{item.price} Credits</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs text-gray-500 mb-3 dark:text-gray-400">{item.description}</p>
                      {owned ? (
                        <div className="space-y-2">
                          <div className="text-xs font-semibold text-emerald-600 bg-emerald-50 py-1.5 px-3 rounded-lg text-center dark:bg-emerald-500/10 dark:text-emerald-400">
                            Bereits gekauft
                            {ownedItemMap[item.id]?.variant && (
                              <span className="ml-2">· {ownedItemMap[item.id].variant}</span>
                            )}
                          </div>
                          {/* Per-item config picker. The Avatar-Emoji
                              item lets the user choose / change the
                              emoji that represents them. Other items
                              that need a variant (Username-Farbe, …)
                              can plug into the same hook later. */}
                          {item.effect === 'avatar_emoji' && (
                            <EmojiPickerInline
                              currentValue={ownedItemMap[item.id]?.variant || ''}
                              onPick={async (emoji) => {
                                const userItem = ownedItemMap[item.id];
                                if (!userItem) return;
                                try {
                                  await api.shop.setVariant(userItem.id, emoji);
                                  toast.success(`Emoji gesetzt: ${emoji}`);
                                  const myRes = await api.shop.getMyItems();
                                  setMyItems(myRes.data); refreshCosmetics?.();
                                } catch { toast.error('Konnte Emoji nicht speichern'); }
                              }}
                            />
                          )}
                          {item.effect === 'custom_title' && (
                            <TextVariantPicker
                              currentValue={ownedItemMap[item.id]?.variant || ''}
                              maxLength={20}
                              placeholder="z.B. Börsen-Experte"
                              onSave={async (text) => {
                                const userItem = ownedItemMap[item.id];
                                if (!userItem) return;
                                try {
                                  await api.shop.setVariant(userItem.id, text);
                                  toast.success(`Titel gesetzt: ${text}`);
                                  const myRes = await api.shop.getMyItems();
                                  setMyItems(myRes.data); refreshCosmetics?.();
                                } catch { toast.error('Konnte Titel nicht speichern'); }
                              }}
                            />
                          )}
                          {item.effect === 'name_color' && (
                            <VariantChips
                              choices={NAME_COLOR_CHOICES}
                              currentValue={ownedItemMap[item.id]?.variant}
                              onPick={async (color) => {
                                const userItem = ownedItemMap[item.id];
                                if (!userItem) return;
                                try {
                                  await api.shop.setVariant(userItem.id, color);
                                  toast.success(`Farbe gesetzt: ${color}`);
                                  const myRes = await api.shop.getMyItems();
                                  setMyItems(myRes.data); refreshCosmetics?.();
                                } catch { toast.error('Konnte Farbe nicht speichern'); }
                              }}
                            />
                          )}
                          {(item.effect === 'profile_frame' || item.effect === 'animated_frame') && (
                            <VariantChips
                              choices={FRAME_COLOR_CHOICES}
                              currentValue={ownedItemMap[item.id]?.variant}
                              onPick={async (color) => {
                                const userItem = ownedItemMap[item.id];
                                if (!userItem) return;
                                try {
                                  await api.shop.setVariant(userItem.id, color);
                                  toast.success(`Rahmen-Farbe: ${color}`);
                                  const myRes = await api.shop.getMyItems();
                                  setMyItems(myRes.data); refreshCosmetics?.();
                                } catch { toast.error('Konnte Farbe nicht speichern'); }
                              }}
                            />
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full text-xs"
                            onClick={async () => {
                              const userItem = ownedItemMap[item.id];
                              if (!userItem) return;
                              try {
                                const res = await api.shop.toggleItem(userItem.id);
                                toast.success(res.data.is_active ? 'Item aktiviert' : 'Item deaktiviert');
                                const myRes = await api.shop.getMyItems();
                                setMyItems(myRes.data); refreshCosmetics?.();
                              } catch { toast.error('Fehler'); }
                            }}
                            data-testid={`shop-toggle-${item.id}`}
                          >
                            {ownedItemMap[item.id]?.is_active ? 'Deaktivieren' : 'Aktivieren'}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          className="w-full"
                          disabled={purchasing === item.id}
                          onClick={() => handlePurchase(item)}
                          data-testid={`shop-buy-${item.id}`}
                        >
                          {purchasing === item.id ? 'Kaufe...' : 'Kaufen'}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ShopPage;
